import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { useMatomo } from "@datapunt/matomo-tracker-react";
import axios from "axios";
import uniqBy from "lodash/uniqBy";
import { useTranslation } from "react-i18next";
import { useHighlight } from "../../hooks/useHighlight";

/** Material-UI */
import {
    alpha,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core";
import { ExpandMore, SaveAlt, Check } from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/styles";

import APIs from "../../APIs";

import { useDocTitle } from "../../components/_common/DocTitle";
import Preloader from "../../components/_common/Preloader";
import Paginator from "../../components/Paginator";
import { generatePDF } from "../../components/Report/functions";
import ReportTMPL from "../../components/Report/TMPL";
import Search from "../../components/Search";
import FilterSystems from "./Filter";
import Infocard from "./Infocard";

const AccessibilityTooltip = React.lazy(() => import("../../components/_common/AccessibilityTooltip"));

// const FILTER_SYSTEMS = false;
const FILTER_SYSTEMS = process.env.REACT_APP_FILTER_SYSTEMS ? JSON.parse(process.env.REACT_APP_FILTER_SYSTEMS) : false;

const DropDownIcon = forwardRef((props, ref) => (
    <IconButton ref={ref} {...props} size={"small"} component={"div"}>
        <ExpandMore color={"primary"} fontSize={"small"} />
    </IconButton>
));
const useStyles = makeStyles((theme) => ({
    bigTitle: {
        margin: theme.spacing(0, 4, 4, 4),
        textAlign: "center",
        justifyContent: "center",
        fontSize: "clamp(1.5rem, 2vw, 2rem)",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.125rem",
            fontWeight: theme.typography.fontWeightMedium,
        },
    },
    alphabet: {
        display: "flex",
        justifyContent: "space-between",
        overflow: "hidden",
    },
    listOverlay: {
        content: '""',
        position: "absolute",
        width: "100%",
        height: 1,
        bottom: -1,
        boxShadow: `0 1px 56px 64px ${theme.palette.background.paper}`,
        zIndex: 1,
    },
    listRoot: {
        maxHeight: "calc(100vh - 0px)",
        overflow: "hidden",
        overflowY: "scroll",
        marginTop: theme.spacing(4),
        paddingBottom: "100vh",
        position: "relative",
        scrollBehavior: "smooth",
    },
    listSection: {
        backgroundColor: "inherit",
    },
    ul: {
        backgroundColor: "inherit",
        padding: 0,
    },
    checkbox: {
        [theme.breakpoints.down("sm")]: {
            padding: 6,
        },
        "&:focus-within ": {
            outline: "#000 auto 2px",
            outlineOffset: 2,
        },
    },
    listSubheaderSticky: {
        backgroundColor: "rgb(234 237 244)",
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(2),
    },
    listItemSelected: {
        backgroundColor: `${alpha(theme.palette.primary.main, 0.25)}!important`,
    },
    alphabetChip: {
        backgroundColor: alpha(theme.palette.primary.light, 0.5),
        color: theme.palette.primary.contrastText,
        "&:hover": {
            backgroundColor: theme.palette.primary.main,
        },
        "&:focus": {
            backgroundColor: theme.palette.primary.main,
        },
    },
    alphabetChipAcvtive: {
        backgroundColor: theme.palette.primary.main,
    },
    alphabetAvatar: {
        backgroundColor: theme.palette.primary.main,
    },
    listItemAvatarAvatar: {
        backgroundColor: alpha(theme.palette.primary.main, 0.25),
    },
    formControl: {
        marginTop: theme.spacing(2),
        minWidth: "100%",
    },
    preRender: {
        position: "fixed",
        left: 0,
        right: "200vw",
        top: "200vh",
        width: "210mm",
    },
    searchWrapper: {
        gap: 16,
        justifyContent: "center",
        "&, & > form": {
            display: "flex",
            alignItems: "center",
        },
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    pdfLoadingWrapper: {
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "rgba(255,255,255,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
    },
    listItemLI: {
        borderBottom: "1px solid rgba(0, 29, 119, 0.125)",
        display: "flex",
        alignItems: "flex-start",
    },
    btnReport: {
        margin: 0,
        height: 48,
        minWidth: 241,
        fontSize: "clamp(12px, 1vw, 14px)",
        "& > span": {
            marginTop: 2,
        },
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 0, 0),
            width: "100%",
            height: 36,
            paddingTop: 4,
        },
    },
    searchForm: {
        width: "100%",
        minHeight: 80,
        display: "flex",
        gap: 16,
    },
    btnSearch: {
        // margin: "16px 8px",
        margin: 0,
        height: 48,
        minWidth: 153,
        "& > span": {
            marginTop: 2,
        },
        // borderRadius: 20,
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },

        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 4, 0),
        },
    },
    listItemTextPrimary: {
        fontWeight: 600,
        fontSize: "clamp(14px, 2vw, 1.25rem)",
        [theme.breakpoints.down("sm")]: {},
    },
    listItemTextSecondary: {
        // fontSize: "0.875rem",
        fontSize: "clamp(13px, 2vw, 1.125rem)",
        color: "#000000DE",
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, -3, 0, -6.5),
        },
    },
    itemTitleLink: {
        fontWeight: 600,
        fontSize: "clamp(14px, 2vw, 1.25rem)",
        // display: "flex",
        display: "contents",
        alignItems: "center",
        gap: 0,
        color: "#001d77",
        textDecoration: "inherit",
        [theme.breakpoints.down("sm")]: {
            alignItems: "flex-start",
            justifyContent: "space-between",
        },
        "&:hover": {
            textDecoration: "underline",
        },
    },
    alert: {
        marginTop: 32,
        marginBottom: 32,
        fontSize: "clamp(13px, 2vw, 1.125rem)",
        "&, & *": {
            color: "#621B16",
            // fontWeight: "bolder",
        },
    },
    toolsWrapper: {
        marginTop: 16,
        padding: theme.spacing(0, 3, 0, 3.75),
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.up("md")]: {
            marginTop: -48,
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 1, 0, 0.75),
            flexWrap: "wrap",
        },
    },
    formControlLabel: {
        [theme.breakpoints.down("sm")]: {
            margin: 0,
        },
    },
    formControlLabelLabel: {
        fontSize: "clamp(12px, 1vw, 1rem)",
        marginLeft: theme.spacing(1.5),
    },
    xsOnly: {
        display: "none",
        [theme.breakpoints.down("xs")]: {
            display: "block",
            width: "100%",
            backgroundColor: "#00000022",
        },
    },
    textField: {
        "& label": {
            fontSize: "clamp(13px, 1vw, 1rem)",
        },
        "& fieldset": {
            [theme.breakpoints.down("sm")]: {
                top: -7,
            },
        },
        "& legend": {
            fontSize: "0.835em",
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.6em",
            },
        },
        "& button": {
            top: "calc(50% - 14px)",
        },
        "& input": {
            left: -1999,
        },
    },
    input: {
        fontSize: "clamp(13px, 1vw, 1rem)",
        paddingTop: 15,
        paddingBottom: 14,
        paddingLeft: 14,
        "& .check": {
            display: "none",
        },
    },
    label: {
        fontSize: "clamp(13px, 1vw, 1rem)",
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
    },
    inputAdornmentEnd: {},
    datePickerButtonRoot: {
        "& svg": {
            color: theme.palette.primary.main,
        },
    },
    adornedEnd: {
        paddingRight: 0,
    },
    selectMenuPaper: {
        opacity: 1,
        '& li[class*="MuiListItem-selected"]': {
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            fontWeight: 500,
        },
    },
}));

const RSISYSTEMY_Page = (props) => {
    const classes = useStyles();
    const { i18n, t } = useTranslation();
    const { highlight } = useHighlight();
    const { trackPageView } = useMatomo();
    const cheAllRef = useRef();
    const history = useHistory();

    useDocTitle(t("pages.systemsPageTitle"));

    const savedPage = Number(localStorage.getItem("sp") || 0);
    const savedPagesPerPage = Number(localStorage.getItem("spp") || 10);

    const regex = /(<([^>]+)>)|\t|\n|&nbsp;/gi;

    const isMobile = useMediaQuery("(max-width: 599.95px)");

    const [apiWait, setApiWait] = useState(true);
    const [loading, setLoading] = useState(false);

    // const [isMobile, setIsMobile] = useState(window.innerWidth < 599.95);
    // const [selected, setSelected] = useState("");
    const [SEARCH, setSEARCH] = useState("");
    const [data, setData] = useState([]);
    const [gestors, setGestors] = useState([]);
    const [loadingPdf, setLoadingPDF] = useState(false);
    const [checked, setChecked] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [page, setPage] = React.useState(savedPage);
    const [rowsPerPage, setRowsPerPage] = React.useState(savedPagesPerPage);
    const [count, setCount] = React.useState(0);
    const [searchError, setSearchError] = React.useState(false);
    const [QUERY, setQUERY] = useState("");
    const [SORT, setSORT] = useState("nazwa_systemu");

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        localStorage.setItem("sp", newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        // setRowsPerPage(parseInt(event.target.value, 10));
        // setPage(0);

        const _rpp = parseInt(event.target.value, 10);
        localStorage.setItem("spp", _rpp);
        setRowsPerPage(_rpp);

        setPage(0);
        localStorage.setItem("sp", 0);
    };

    /** Process url query */
    useEffect(() => {
        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        if (!Object.keys(currentQuery)?.length) {
            setApiWait(false);
            return;
        }

        if (
            Number(currentQuery?.page) !== page ||
            Number(currentQuery?.limit) !== rowsPerPage ||
            currentQuery?.search !== encodeURI(SEARCH)
        ) {
            if (Number(currentQuery?.limit) !== rowsPerPage) {
                setRowsPerPage(Number(currentQuery?.limit));
            }

            if (currentQuery?.search !== encodeURI(SEARCH)) {
                setSEARCH(currentQuery?.search ? decodeURI(currentQuery?.search) : "");
            }

            if (Number(currentQuery?.page) !== page) {
                setPage(Number(currentQuery?.page) - 1);
            }

            setApiWait(false);
        } else {
            setApiWait(false);
        }
        // eslint-disable-next-line
    }, []);

    /** get api data */
    useEffect(() => {
        if (apiWait) return;
        setSearchError(false);
        getData({ page });
        return;
        // eslint-disable-next-line
    }, [SEARCH, page, rowsPerPage, apiWait]);

    /* track page view */
    useEffect(() => {
        trackPageView();
        // eslint-disable-next-line
    }, []);

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleToggleChecked = (event) => {
        const _checked = event;

        const all = data?.flatMap((s) => s.id_systemu);

        setChecked(_checked ? all : []);
    };

    const exportPdf = async (PDFOptions) => {
        const selectedData = data
            .filter((d) => checked.includes(d.id_systemu))
            .sort((a, b) => a.gestor_level - b.gestor_level || a.nazwa_systemu.localeCompare(b.nazwa_systemu));

        let pdfData = [];

        setLoadingPDF(true);

        selectedData.forEach((item) => {
            axios
                .get(`${APIs.dataURL.rsisystemy}/${item.id_systemu}`)
                .then((result) => {
                    pdfData.push({
                        ...item,
                        ...result.data,
                    });
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    if (selectedData.length === pdfData.length) {
                        setReportData(
                            pdfData.sort(
                                (a, b) =>
                                    a.gestor_level - b.gestor_level || a.nazwa_systemu.localeCompare(b.nazwa_systemu)
                            )
                        );

                        setTimeout(() => {
                            generatePDF({
                                data: pdfData.sort(
                                    (a, b) =>
                                        a.gestor_level - b.gestor_level ||
                                        a.nazwa_systemu.localeCompare(b.nazwa_systemu)
                                ),
                                gestors,
                                lang: i18n.language,
                            });
                            setLoadingPDF(false);
                        }, 1234);
                    }
                });
        });

        return;
    };

    const handleGetReport = () => {
        setChecked([]);
        if (!checked.length) return;
        exportPdf({
            abouts: data.filter((d) => checked.includes(d.id_systemu)),
        });
    };

    const handleOnSubmitSearch = (newSearchText) => {
        setPage(0);
        setSEARCH(newSearchText);
    };

    const handleOnFilter = async (args = {}) => {
        const { query } = args;

        setApiWait(true);

        await Promise.all([setPage(0), query && setQUERY(query)]).then((res) => {
            setApiWait(false);
            setLoading(false);
        });
    };

    const handleSort = async (event) => {
        setSORT(event.target.value);
        const ordering = event.target.value;

        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));
        const newQuery = {
            ...currentQuery,
            ordering,
        };

        setApiWait(true);

        await Promise.all([
            // setPage(0),
            newQuery && setQUERY(newQuery),
        ]).then((res) => {
            setApiWait(false);
            setLoading(false);
        });
    };

    const getData = ({ page }) => {
        // const newQuery =
        //     QUERY || new URLSearchParams({ limit: rowsPerPage, page: page + 1, search: SEARCH || "" }).toString();

        let currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        const newQuery = new URLSearchParams(
            QUERY || { ...currentQuery, limit: rowsPerPage, page: page + 1, search: SEARCH || "" }
        ).toString();

        let apiURL = `${APIs.dataURL.rsisystemy}?${newQuery}`;

        const newPath = `${history.location.pathname}?${newQuery}${history.location.hash}`;

        setTimeout(() => {
            history.push(newPath, { shallow: true });
        }, 100);

        setLoading(true);

        axios
            .get(apiURL)
            .then((result) => {
                const gestor_levels = {
                    "Administracja terenowa": { name: "Administracja terenowa", level: 3 },
                    "Pozostała administracja centralna": { name: "Pozostała administracja centralna", level: 2 },
                    Ministerstwo: { name: "Ministerstwo", level: 1 },
                };

                setCount(result.data.count);

                if (SEARCH && !result?.data?.count) {
                    setSearchError(t('Brak wyników wyszukiwania dla frazy "{{SEARCH}}"{{dot}}', { SEARCH, dot: "." }));
                }

                const apiData = result.data.results.map((item) => ({
                    ...item,
                    gestor_level: gestor_levels[item.gestor_typ]?.level,
                    nazwa_systemu: item.nazwa_systemu?.replace(/(<([^>]+)>)|\t|\n/gi, ""),
                }));
                // .sort((a, b) => -b.nazwa_systemu?.toUpperCase().localeCompare(a.nazwa_systemu?.toUpperCase()));

                setData(apiData);

                const _gestors = uniqBy(
                    apiData.map((a) => ({
                        gestor_typ: a.gestor_typ,
                        gestor_level: a.gestor_level,
                        gestor: a.gestor,
                    })),
                    "gestor"
                );

                setGestors(_gestors);
            })

            .catch((error) => {
                if (SEARCH) {
                    setSearchError(
                        t('Brak wyników wyszukiwania dla frazy "{{SEARCH}}"{{dot}}<br/>{{apiError}}', {
                            SEARCH,
                            dot: ".",
                            apiError: error.response.data.detail ? `<em>${error.response.data.detail}</em>` : "",
                        })
                    );
                } else {
                    setSearchError(error.response?.data?.detail || t("Nieznany błąd"));
                }
            })
            .finally(() => {
                setLoading(false);
                setQUERY("");
            });
    };

    const optionsSort = [
        { label: t("Nazwa od A do Z"), value: "nazwa_systemu", default: true },
        { label: t("Nazwa od Z do A"), value: "-nazwa_systemu" },
        { label: t("Data aktualizacji rosnąco"), value: "DA", date: true },
        { label: t("Data aktualizacji malejąco"), value: "DD", date: true },
    ];

    return (
        <>
            <Typography
                variant="h4"
                className={classes.bigTitle}
                color="primary"
                component={"h2"}
                id={"page-content"}
                tabIndex={0}
            >
                {t("pages.systems")}
            </Typography>

            {!!loadingPdf && (
                <div className={classes.pdfLoadingWrapper}>
                    <img src={"/images/preloader.gif"} style={{ width: 128 }} alt={"Loading..."} />
                </div>
            )}

            {!!reportData.length && (
                <div className={classes.preRender}>
                    <ReportTMPL reportData={reportData} />
                </div>
            )}

            <FilterSystems onFilter={handleOnFilter} filterOn={FILTER_SYSTEMS} />

            <Grid container spacing={0}>
                <Grid item xs={12} className={classes.searchWrapper}>
                    {!!FILTER_SYSTEMS ? null : (
                        <Search
                            label={t("common.searchRSISYSTEMY")}
                            onSubmit={(newSearchText) => handleOnSubmitSearch(newSearchText)}
                            error={searchError}
                            search={SEARCH}
                        />
                    )}

                    <Button
                        variant={"contained"}
                        color={"primary"}
                        tabIndex={0}
                        onClick={handleGetReport}
                        className={classes.btnReport}
                        disabled={!checked.length}
                        aria-label={t(
                            "Pobierz raport PDF{{dot}} Zaznacz poniżej przynajmniej jeden checkbox, aby pobrać raport",
                            { dot: "." }
                        )}
                        aria-disabled={true}
                        startIcon={<SaveAlt />}
                        style={{ marginBottom: FILTER_SYSTEMS ? 40 : undefined }}
                    >
                        {"Pobierz raport pdf"}
                    </Button>
                </Grid>

                <Grid item xs={12} style={{ marginTop: 40, marginBottom: 25 }}>
                    <TextField
                        id="systems-sort"
                        select
                        label={t("Sortowanie")}
                        value={SORT}
                        onChange={handleSort}
                        variant="outlined"
                        fullWidth={!!isMobile}
                        SelectProps={{
                            IconComponent: DropDownIcon,
                            MenuProps: {
                                getContentAnchorEl: null,
                                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                                transformOrigin: { vertical: "top", horizontal: "center" },
                                PaperProps: {
                                    className: classes.selectMenuPaper,
                                },
                            },
                            id: "systems-sort-select",
                        }}
                        size={isMobile ? "small" : "medium"}
                        classes={{ root: classes.textField }}
                        inputProps={{
                            className: classes.input,
                        }}
                        style={{
                            order: isMobile ? 1 : 2,
                            width: isMobile ? undefined : "clamp(280px, 20vw, 328px)",
                        }}
                        ref={(ref) => {
                            if (ref) {
                                const inputEl = ref.querySelector("input");

                                if (inputEl) {
                                    setTimeout(() => {
                                        inputEl.style.color = "#000";
                                        inputEl.style.backgroundColor = "#FFF";
                                        inputEl.style.opacity = 1;
                                        inputEl.id = "systems-sort";
                                    }, 500);
                                }
                            }
                        }}
                    >
                        {optionsSort.map((option) => {
                            const _hasDateField = data?.every((d) => d.hasOwnProperty("data_aktualizacji"));

                            return option.date && !_hasDateField ? null : (
                                <MenuItem key={option.value} value={option.value}>
                                    <Box
                                        // component={option.default ? "em" : "span"}
                                        className={classes.label}
                                    >
                                        {option.label}
                                        {option.value === SORT && <Check className="check" color="primary" />}
                                    </Box>
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </Grid>

                <Grid item xs={12} style={{ position: "relative", overflow: "hidden" }} id="aGrid">
                    {!!searchError && (
                        <Alert severity="error" className={classes.alert}>
                            <span dangerouslySetInnerHTML={{ __html: searchError }} />
                        </Alert>
                    )}

                    {!!loading && (
                        <>
                            <Preloader type="linear" />
                            <Preloader type="linear" />
                            <Preloader type="linear" />
                        </>
                    )}

                    {/* {!!dataSorted.length && !loading && ( */}
                    {!!data.length && !loading && (
                        <Paginator
                            component="div"
                            count={count}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            queryParams={{ limit: rowsPerPage, page: page + 1, search: SEARCH }}
                            componentTopBefore={
                                <FormControlLabel
                                    inputRef={cheAllRef}
                                    control={
                                        <Checkbox
                                            color={"primary"}
                                            checked={checked?.length === data?.length}
                                            indeterminate={!!(checked?.length && checked?.length !== data?.length)}
                                            size={isMobile ? "small" : "medium"}
                                            onClick={(e) => handleToggleChecked(e.target.checked)}
                                        />
                                    }
                                    label={
                                        checked?.length === data?.length
                                            ? t("Odznacz wszystkie na stronie")
                                            : t("Zaznacz wszystkie na stronie")
                                    }
                                    labelPlacement="end"
                                    classes={{ root: classes.formControlLabel, label: classes.formControlLabelLabel }}
                                    style={{
                                        // order: isMobile ? 2 : 1
                                        marginLeft: 4,
                                    }}
                                />
                            }
                        >
                            <Divider style={{ marginTop: isMobile ? 0 : 16 }} />

                            <List className={classes.listRoot0} dense={true} id="aList">
                                {/* {dataSorted?.map((item, key) => { */}
                                {data?.map((item, key) => {
                                    return (
                                        <ListItem
                                            dense={true}
                                            className={classes.listItemLI}
                                            key={key}
                                            id={`item-${item.id_systemu}`}
                                            onClick={handleToggle(item.id_systemu)}
                                        >
                                            <ListItemIcon style={{ minWidth: isMobile ? "unset" : 40 }}>
                                                <AccessibilityTooltip
                                                    arrow={false}
                                                    placement="left"
                                                    title={
                                                        checked.indexOf(item.id_systemu) !== -1
                                                            ? `Usuń z rapotu`
                                                            : `Dodaj do raportu`
                                                    }
                                                >
                                                    <Checkbox
                                                        edge="start"
                                                        className={classes.checkbox}
                                                        checked={checked.indexOf(item.id_systemu) !== -1}
                                                        id={`item-${item.id_systemu}-checkbox`}
                                                        disableRipple
                                                        inputProps={{
                                                            tabIndex: 0,
                                                            "aria-labelledby": item.id_systemu,
                                                        }}
                                                        color={"primary"}
                                                        size={isMobile ? "small" : "medium"}
                                                    />
                                                </AccessibilityTooltip>
                                            </ListItemIcon>
                                            <ListItemText
                                                id={item.id_systemu}
                                                disableTypography
                                                primary={
                                                    <a
                                                        href={`/${i18n.language}/rsisystemy/${item.id_systemu}`}
                                                        rel="noopener noreferrer"
                                                        className={classes.itemTitleLink}
                                                    >
                                                        {highlight({
                                                            text: item.nazwa_systemu,
                                                            search: SEARCH,
                                                            classes,
                                                        })}

                                                        {!!item.api && (
                                                            <img
                                                                src="/icons/API-chips.png"
                                                                alt=""
                                                                style={{
                                                                    marginTop: isMobile ? "0.125em" : 0,
                                                                    height: isMobile ? 20 : 24,
                                                                    width: "auto",
                                                                }}
                                                            />
                                                        )}
                                                    </a>
                                                }
                                                // secondary={`${
                                                //     item.nazwa_systemu_skr
                                                //         ? item.nazwa_systemu_skr.replace(regex, "") + ", "
                                                //         : ""
                                                // } ${item.gestor ? item.gestor.replace(regex, "") : ""}`}

                                                secondary={<Infocard item={item} regex={regex} isMobile={isMobile} />}
                                                classes={{
                                                    primary: classes.listItemTextPrimary,
                                                    secondary: classes.listItemTextSecondary,
                                                }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Paginator>
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default RSISYSTEMY_Page;
